//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ethers, EventLog, Signer } from 'ethers'

import { ActivityEnum, AlertLevelEnum, ONE_DAY, RoleEnum } from '@shared/Enums'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { APR, otAssert } from '@shared/conversions'

import { LoanFactoryAbi, PoolAbi } from '@shared/Abis'
import { AlertCTO } from '@shared/AlertCTO'

import { alertActions } from '@react/redux/alertSlice'
import { poolActions } from '@react/redux/poolSlice'
import { dateToEthDate, EthAddr, ProviderWritable, TxHash } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { ILoanSettingsStruct } from '@typechain-types/contracts/Loan'
import { LoanFactory } from '@typechain-types/index'
import { PerimeterChainAddressesInterface } from '@react/utils/PerimeterAddresses'

export interface CreateLoanOpts {
  poolAddr: EthAddr
  accrualStartDayTimestamp: number
  transferInWindowDurationDays: number
  transferOutWindowDurationDays: number
  indicativeApr: APR
  finalizedApr: APR
  tenor: number
  originationBps: number
  priorLoanAddr: EthAddr
}

export const createLoanTx =
  (props: CreateLoanOpts): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const activity = ActivityEnum.LOAN_CREATE

    try {
      logOT.debug('createLoanSaga 1')
      const { poolAddr } = props

      const { webSocket, userConnection } = getState().chainState

      const { providerReadOnly, perimeterAddresses } = getState().chainState.chainConfiguration
      otAssert(perimeterAddresses, 'perimeterAddresses is not defined')
      otAssert(providerReadOnly, 'providerReadOnly is not defined')
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      otAssert(userConnection, 'userConnection is not defined')
      const { roleEnum, signer } = userConnection
      assertRole(roleEnum, RoleEnum.BORROWER)

      const loanAddr = await createLoan(perimeterAddresses, webSocket, signer as Signer, props, chainConfigurationName)

      dispatch(poolActions.createLoanSuccess({ poolAddr, loanAddr }))

      const successAlert: AlertCTO = {
        sourceObjectAddr: loanAddr,
        title: 'Loan successfully created.',
        description: 'Loan will still need to be approved by the Loan Vault admin.',
        createdAt: dateToEthDate(new Date()),
        dueDateTime: 0,
        alertLevel: AlertLevelEnum.SUCCESS,
      }
      dispatch(alertActions.addAlert(successAlert))
    } catch (e: any) {
      await dispatchError(dispatch, e, activity, 4000)
    }
  }

export const createLoan = async (
  perimeterAddresses: PerimeterChainAddressesInterface,
  webSocket: WebSocket | undefined,
  borrowerSigner: Signer,
  opts: CreateLoanOpts,
  chainConfigurationName: string
): Promise<EthAddr> => {
  logOT.debug(
    'createLoan ',
    opts.accrualStartDayTimestamp,
    opts.transferInWindowDurationDays,
    opts.indicativeApr,
    opts.tenor,
    opts.priorLoanAddr
  )

  const providerWritable = borrowerSigner.provider as ProviderWritable
  const borrowerAddr: EthAddr = await borrowerSigner.getAddress()
  const {
    poolAddr,
    indicativeApr,
    finalizedApr,
    tenor,
    transferOutWindowDurationDays,
    originationBps,
    accrualStartDayTimestamp,
    transferInWindowDurationDays,
    priorLoanAddr,
  } = opts

  const uuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.LOAN_CREATE, poolAddr, uuid, chainConfigurationName))

  const loanSettings: ILoanSettingsStruct = {
    principal: 0,
    startingPrincipal: 0,
    indicativeApr: Number(indicativeApr),
    finalizedApr: Number(finalizedApr),
    durationDays: tenor,
    dropDeadTimestamp: accrualStartDayTimestamp + (tenor + transferOutWindowDurationDays + 10) * ONE_DAY,
    originationBps,
    accrualStartDayTimestamp,
    transferInWindowDurationDays,
    transferOutWindowDurationDays,
    priorLoan: priorLoanAddr,
  }

  const loanFactoryContract = new ethers.Contract(perimeterAddresses.LoanFactory, LoanFactoryAbi, providerWritable)

  // struct ILoanSettings {
  //   enum ILoanType loanType;
  //   uint256 principal;
  //   uint256 apr;
  //   uint256 duration;
  //   uint256 paymentPeriod;
  //   uint256 dropDeadTimestamp;
  //   uint256 latePayment;
  //   uint256 originationBps;
  // }
  // ```solidity
  // enum ILoanType {
  //   Fixed,
  //   Open,
  //   Rollover
  // }

  logOT.debug('createLoan loanSettings', loanSettings)

  const poolContract = new ethers.Contract(poolAddr, PoolAbi, providerWritable)
  const liquidityAsset = await poolContract.liquidityAssetAddr()

  const tx = await (loanFactoryContract.connect(borrowerSigner) as unknown as LoanFactory).createLoan(
    borrowerAddr,
    poolAddr,
    liquidityAsset,
    loanSettings
  )

  webSocket?.send(
    wsServerActionPoolTX(ActivityEnum.LOAN_CREATE, poolAddr, uuid, tx.hash as TxHash, chainConfigurationName)
  )
  const receipt = await tx.wait(1)

  const loanCreatedEvent = (receipt?.logs as unknown as EventLog[])?.find(
    (event: EventLog) => event.eventName === 'LoanCreated'
  ) as EventLog

  logOT.debug('createLoan LoanCreated Event ', loanCreatedEvent)

  const loanAddr: EthAddr = loanCreatedEvent.args.addr as EthAddr
  logOT.debug('createLoan loadAddr', loanAddr)
  return loanAddr
}
